import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CustomerNotes } from "@app/models/customer-notes";
import { CustomerNoteService } from "@app/services/customer-note.service";

@Component({
    selector: "app-select-customer-note-popup",
    templateUrl: "./select-customer-note-popup.component.html",
    styleUrls: ["./select-customer-note-popup.component.css"],
    standalone: false
})
export class SelectCustomerNotePopupComponent implements OnInit {
  @Input() customerNotes: Array<CustomerNotes>;
  @Input() displaySearchDialog: boolean;
  @Input() selectedCustomerNotes: Array<CustomerNotes>;
  @Output() onSubmit = new EventEmitter<Array<CustomerNotes>>();
  @Output() onCancel = new EventEmitter();

  constructor(private customerNoteService: CustomerNoteService) {}

  ngOnInit() {
    if (!this.selectedCustomerNotes) {
      this.selectedCustomerNotes = [];
    } else {
      this.selectedCustomerNotes = [...this.selectedCustomerNotes];
    }
    this.syncInitialSelection();
  }

  syncInitialSelection() {
    if (!this.customerNotes || this.customerNotes.length === 0) {
      return;
    }

    const currentList = [...this.selectedCustomerNotes];
    for (const note of currentList) {
      const fullNote = this.customerNoteService.findCustomerNoteById(note.id, this.customerNotes) || note;
      const allChildren = this.findAllChidren(fullNote);
      for (const child of allChildren) {
        if (!this.selectedCustomerNotes.some((selected) => selected.id === child.id)) {
          this.selectedCustomerNotes.push(child);
        }
      }
    }

    this.updateAllParentsSelection();
  }

  hideSearch() {
    this.onCancel.emit();
  }

  onSelect(customerNote: CustomerNotes) {
    if (!customerNote) {
      return;
    }

    const fullNote = this.customerNoteService.findCustomerNoteById(customerNote.id, this.customerNotes) || customerNote;
    const allDescendants = this.findAllChidren(fullNote);
    const updated = [...this.selectedCustomerNotes];
    for (const item of allDescendants) {
      if (!updated.some((selected) => selected.id === item.id)) {
        updated.push(item);
      }
    }
    this.selectedCustomerNotes = updated;

    this.updateAllParentsSelection();
  }

  onSave() {
    this.onSubmit.emit(this.selectedCustomerNotes);
  }

  deleteSelectedCustomerNote(customerNote: CustomerNotes) {
    if (!customerNote) {
      return;
    }

    const fullNote = this.customerNoteService.findCustomerNoteById(customerNote.id, this.customerNotes) || customerNote;
    const removeIds = new Set(this.findAllChidren(fullNote).map((item) => item.id));

    this.selectedCustomerNotes = this.selectedCustomerNotes.filter((item) => !removeIds.has(item.id));
  }

  updateAllParentsSelection() {
    if (!this.customerNotes || this.customerNotes.length === 0) {
      return;
    }

    const selectedSet = new Set(this.selectedCustomerNotes.map((note) => note.id));

    const checkNode = (node: CustomerNotes): boolean => {
      if (!node.subCustomerNotes || node.subCustomerNotes.length === 0) {
        return selectedSet.has(node.id);
      }

      let allChildrenSelected = true;
      for (const child of node.subCustomerNotes) {
        if (!checkNode(child)) {
          allChildrenSelected = false;
        }
      }

      if (allChildrenSelected) {
        selectedSet.add(node.id);
      } else {
        selectedSet.delete(node.id);
      }

      return allChildrenSelected;
    };

    for (const rootNode of this.customerNotes) {
      checkNode(rootNode);
    }

    const newSelected: CustomerNotes[] = [];
    for (const item of this.selectedCustomerNotes) {
      if (selectedSet.has(item.id) && !newSelected.some((note) => note.id === item.id)) {
        newSelected.push(item);
      }
    }
    for (const id of selectedSet) {
      if (!newSelected.some((note) => note.id === id)) {
        const found = this.customerNoteService.findCustomerNoteById(id, this.customerNotes);
        if (found) {
          newSelected.push(found);
        }
      }
    }

    this.selectedCustomerNotes = newSelected;
  }

  findAllChidren(customerNote: CustomerNotes): CustomerNotes[] {
    let items: CustomerNotes[] = [];
    if (customerNote) {
      items.push(customerNote);
      if (customerNote.subCustomerNotes && customerNote.subCustomerNotes.length > 0) {
        customerNote.subCustomerNotes.forEach((p) => {
          items = items.concat(this.findAllChidren(p));
        });
      }
    }
    return items;
  }
}
